import { useEffect, useState } from "react";
import { db, type Product } from "../db";
import { allStock, fmtDate } from "../lib";
import { Shell, type ScreenProps } from "./common";

type Row = { product: Product; issued: number; left: number };

export default function Stock({ updateReady, back, state, user }: ScreenProps) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [recent, setRecent] = useState<{ at: string; text: string }[]>([]);

  useEffect(() => {
    void (async () => {
      const [left, moves, products] = await Promise.all([
        allStock(user.id), db.stockMoves.toArray(), db.products.toArray(),
      ]);
      const byId = new Map(products.map((p) => [p.id, p]));

      const issued = new Map<string, number>();
      for (const m of moves) {
        if (m.userId !== user.id || m.deletedAt || m.qty <= 0) continue;
        issued.set(m.productId, (issued.get(m.productId) ?? 0) + m.qty);
      }

      setRows([...left.entries()]
        .map(([pid, l]) => ({ product: byId.get(pid)!, issued: issued.get(pid) ?? 0, left: l }))
        .filter((r) => r.product)
        .sort((a, b) => a.product.sortOrder - b.product.sortOrder));

      setRecent(moves
        .filter((m) => m.userId === user.id && !m.deletedAt)
        .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))
        .slice(0, 12)
        .map((m) => ({
          at: m.createdAt ?? "",
          text: `${m.qty > 0 ? "выдано" : "списано"} ${Math.abs(m.qty)} — ${byId.get(m.productId)?.name ?? "позиция"}`,
        })));
    })();
  }, [user.id, state.lastSync, state.pending]);

  return (
    <Shell title="Мои остатки" onBack={back} state={state} updateReady={updateReady}>
      <div className="hint" style={{ marginBottom: 14 }}>
        {user.name} · обновлено при последней синхронизации
      </div>

      {rows === null && <div className="empty">Загрузка…</div>}
      {rows?.length === 0 && (
        <div className="empty">
          На руках ничего не числится.<br />Выдачу оформляет руководитель.
        </div>
      )}

      <div className="list">
        {rows?.map((r) => {
          const low = r.issued > 0 && r.left / r.issued < 0.3;
          return (
            <div className="item" key={r.product.id} style={{ cursor: "default" }}>
              <span className="main">
                <span className="name">{r.product.name}</span>
                <span className="meta">
                  {r.product.article ? "арт. " + r.product.article + " · " : ""}
                  {r.product.kind}{r.issued ? " · выдано " + r.issued : ""}
                </span>
              </span>
              <span className={"pill " + (r.left < 0 ? "c" : low ? "w" : "n")}>
                осталось {r.left}
              </span>
            </div>
          );
        })}
      </div>

      {rows && rows.length > 0 && (
        <div className="note">
          Позиции, где осталось меньше трети от выданного, подсвечены — пора просить пополнение.
        </div>
      )}

      {recent.length > 0 && (
        <>
          <div className="sub">Последние движения</div>
          {recent.map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderTop: "1px solid var(--border)" }}>
              <span className="hint" style={{ minWidth: 82, flex: "none" }}>
                {m.at ? fmtDate(m.at) : ""}
              </span>
              <span style={{ fontSize: 14, color: "var(--ink-2)" }}>{m.text}</span>
            </div>
          ))}
        </>
      )}
    </Shell>
  );
}
